import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge } from '../atoms';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * CommandBar — the global ⌘K / Ctrl-K command-bar search (PRD 06.3, PRD 2.4).
 *
 * Mounted once at the app shell. Opens three ways: the ⌘K / Ctrl-K shortcut,
 * "/" (when not typing in a field), or the visible SearchTrigger in the top bar
 * (which dispatches a `commandbar:open` CustomEvent this component listens for).
 * A centered overlay with a single search box queries the REAL cross-entity
 * endpoint (/api/search) and lets the operator jump straight to a client,
 * parcel, or property — Cindy's "jump to an account without walking 20 steps".
 *
 * - Debounced live query to /api/search?q=&limit=. The backend view-gates each
 *   entity segment, so results only include surfaces the caller can view.
 * - Results are GROUPED by entity type under section headers (Clients /
 *   Properties / Parcels), demo-scoped to those three core types.
 * - A "Quick add" section (Create client / Create property) is appended, gated
 *   by the caller's create permissions, so the palette is a create shortcut too.
 * - Full keyboard control: ↑/↓ traverse the unified list (results + actions),
 *   Enter opens/runs the active row, Esc closes.
 */

interface SearchResult {
  entity_type: 'client' | 'parcel' | 'property' | string;
  id: string;
  label: string;
  sublabel: string | null;
  archived?: boolean; // §9 — archived property/parcel results stay searchable, flagged
  status: string | null;
  badges: string[];
  meta: Record<string, unknown>;
}

interface SearchResponse {
  results: SearchResult[];
}

/**
 * Row is the discriminated union the keyboard model traverses: a `result` (a
 * search hit) or a static `action` (quick-add). Modeling both as one list keeps
 * ↑/↓/Enter a single index over `rows` rather than two parallel selection paths.
 */
type Row =
  | { kind: 'result'; key: string; data: SearchResult }
  | { kind: 'action'; key: string; label: string; run: () => void };

// entity_type → { detail route builder, human label }. Adding a searchable
// entity type means adding a spec here AND a matching ENTITY_SPECS entry in
// src/routes/search.js — the two well-known spots; a row with no route here
// renders but navigates nowhere.
const ENTITY_ROUTE: Record<string, { path: (id: string) => string; label: string }> = {
  client: { path: (id) => `/clients/${id}`, label: 'Client' },
  parcel: { path: (id) => `/parcels/${id}`, label: 'Parcel' },
  property: { path: (id) => `/properties/${id}`, label: 'Property' },
};

// Section-header label per entity type (plural). Groups render in this fixed
// order; unknown types fall back to a capitalized-plural label after these.
const GROUP_LABEL: Record<string, string> = {
  client: 'Clients',
  property: 'Properties',
  parcel: 'Parcels',
};
const GROUP_ORDER = ['client', 'property', 'parcel'];

function groupLabel(type: string): string {
  return GROUP_LABEL[type] ?? `${type.charAt(0).toUpperCase()}${type.slice(1)}s`;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function CommandBar() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQ = useDebounced(q.trim(), 200);

  // Quick-add create actions, gated on the caller's create permission (default-
  // DENY while the grid loads) so a read-only role never sees a dead-end action.
  const canAddClient = useEntityPermission('client', 'can_add');
  const canAddProperty = useEntityPermission('property', 'can_add');
  const canAddParcel = useEntityPermission('parcel', 'can_add');

  // Global open shortcut: ⌘K / Ctrl-K anywhere; "/" only when not typing.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
        return;
      }
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (!open && e.key === '/' && !typing) {
        e.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Visible trigger: the top-bar SearchTrigger dispatches `commandbar:open`.
  // Separate effect (empty deps) so it subscribes once, independent of `open`.
  useEffect(() => {
    function onOpen() {
      setOpen(true);
    }
    document.addEventListener('commandbar:open', onOpen);
    return () => document.removeEventListener('commandbar:open', onOpen);
  }, []);

  // Reset + focus when opened.
  useEffect(() => {
    if (open) {
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    setQ('');
  }, [open]);

  const searchQuery = useQuery<SearchResponse>({
    queryKey: ['command-search', debouncedQ],
    queryFn: ({ signal }) => api.get<SearchResponse>(`/api/search?q=${encodeURIComponent(debouncedQ)}&limit=20`, signal),
    enabled: open && debouncedQ.length > 0,
    staleTime: 15_000,
  });

  const results = useMemo(() => searchQuery.data?.results ?? [], [searchQuery.data]);

  // Group results by entity type for display: order groups by GROUP_ORDER (then
  // first-appearance for unknown types), preserving the backend's within-type
  // rank order (Array.sort is stable; equal keys keep input order).
  const orderedResults = useMemo(() => {
    const firstSeen = new Map<string, number>();
    results.forEach((r, i) => {
      if (!firstSeen.has(r.entity_type)) firstSeen.set(r.entity_type, i);
    });
    const priority = (t: string) => {
      const idx = GROUP_ORDER.indexOf(t);
      return idx === -1 ? GROUP_ORDER.length : idx;
    };
    return [...results].sort((a, b) => {
      const pa = priority(a.entity_type);
      const pb = priority(b.entity_type);
      if (pa !== pb) return pa - pb;
      if (a.entity_type !== b.entity_type) {
        return (firstSeen.get(a.entity_type) ?? 0) - (firstSeen.get(b.entity_type) ?? 0);
      }
      return 0;
    });
  }, [results]);

  const quickAddActions = useMemo(() => {
    const acts: { key: string; label: string; run: () => void }[] = [];
    if (canAddClient) acts.push({ key: 'quick-add-client', label: 'Create client', run: () => navigate('/clients/new') });
    if (canAddProperty) acts.push({ key: 'quick-add-property', label: 'Create property', run: () => navigate('/properties/new') });
    if (canAddParcel) acts.push({ key: 'quick-add-parcel', label: 'Create parcel', run: () => navigate('/parcels/new') });
    return acts;
  }, [canAddClient, canAddProperty, canAddParcel, navigate]);

  // The unified, ordered list the keyboard model indexes into: grouped results
  // first, then quick-add actions.
  const rows = useMemo<Row[]>(() => {
    const resultRows: Row[] = orderedResults.map((data) => ({
      kind: 'result',
      key: `${data.entity_type}:${data.id}`,
      data,
    }));
    const actionRows: Row[] = quickAddActions.map((a) => ({
      kind: 'action',
      key: a.key,
      label: a.label,
      run: a.run,
    }));
    return [...resultRows, ...actionRows];
  }, [orderedResults, quickAddActions]);

  // Keep active index in range as results change.
  useEffect(() => { setActive(0); }, [debouncedQ]);

  // Clamp for render/keyboard so a stale index (rows shrank) never dereferences
  // undefined. -1 when there's nothing selectable.
  const activeIndex = rows.length === 0 ? -1 : Math.min(active, rows.length - 1);

  function go(r: SearchResult) {
    const route = ENTITY_ROUTE[r.entity_type];
    if (!route) return;
    setOpen(false);
    navigate(route.path(r.id));
  }

  function activate(row: Row) {
    if (row.kind === 'result') {
      go(row.data);
      return;
    }
    setOpen(false);
    row.run();
  }

  function onListKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[activeIndex];
      if (row) activate(row);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  // The section header (if any) that precedes the row at index i: the group
  // label when a result row begins a new entity-type group, and "Quick add"
  // before the first action row.
  function headerBefore(i: number): string | null {
    const row = rows[i];
    const prev = i > 0 ? rows[i - 1] : undefined;
    if (row.kind === 'action') {
      return !prev || prev.kind !== 'action' ? 'Quick add' : null;
    }
    const type = row.data.entity_type;
    const prevType = prev && prev.kind === 'result' ? prev.data.entity_type : null;
    return prevType === type ? null : groupLabel(type);
  }

  if (!open) return null;

  const sectionHeaderStyle: React.CSSProperties = {
    padding: 'var(--sp-2) var(--sp-3) var(--sp-1)',
    fontSize: 'var(--fs-xs)',
    fontWeight: 'var(--fw-semibold, 600)',
    color: 'var(--color-ink-3)',
    textTransform: 'uppercase',
    letterSpacing: 'var(--ls-wide, 0.04em)',
  };

  const optionStyle = (isActive: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--sp-3)',
    width: '100%',
    textAlign: 'left',
    padding: 'var(--sp-2) var(--sp-3)',
    border: 'none',
    borderRadius: 'var(--r-md)',
    background: isActive ? 'var(--color-bg)' : 'transparent',
    color: 'var(--color-ink)',
    fontFamily: 'inherit',
    cursor: 'pointer',
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(15,23,24,0.4)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
      }}
    >
      <div
        style={{
          width: 'min(560px, 92vw)',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-lg)',
          boxShadow: '0 16px 48px rgba(15,23,24,0.28)',
          overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', padding: '0 var(--sp-3)', borderBottom: '1px solid var(--color-border)' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-ink-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onListKeyDown}
            placeholder="Search clients, parcels, properties…"
            aria-label="Search clients, parcels, properties"
            aria-controls="command-bar-results"
            style={{
              flex: 1,
              padding: 'var(--sp-3) 0',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--color-ink)',
              fontFamily: 'inherit',
              fontSize: 'var(--fs-lg)',
            }}
          />
          <kbd style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-sm, 4px)', padding: '1px 6px' }}>Esc</kbd>
        </div>

        <div id="command-bar-results" role="listbox" style={{ maxHeight: 380, overflowY: 'auto', padding: 'var(--sp-2)' }}>
          {debouncedQ.length === 0 && (
            <div style={{ padding: 'var(--sp-3) var(--sp-4)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', textAlign: 'center' }}>
              Type to search across clients, parcels, and properties.
            </div>
          )}
          {debouncedQ.length > 0 && searchQuery.isLoading && (
            <div style={{ padding: 'var(--sp-4)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)', textAlign: 'center' }}>
              Searching…
            </div>
          )}
          {debouncedQ.length > 0 && searchQuery.isError && (
            <div style={{ padding: 'var(--sp-4)', color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)', textAlign: 'center' }}>
              Search failed. Try again.
            </div>
          )}
          {debouncedQ.length > 0 && !searchQuery.isLoading && !searchQuery.isError && results.length === 0 && (
            <div style={{ padding: 'var(--sp-4)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)', textAlign: 'center' }}>
              No results for “{debouncedQ}”.
            </div>
          )}

          {rows.map((row, i) => {
            const isActive = i === activeIndex;
            const header = headerBefore(i);
            return (
              <Fragment key={row.key}>
                {header && <div style={sectionHeaderStyle} aria-hidden="true">{header}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  data-testid={row.kind === 'action' ? row.key : undefined}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => activate(row)}
                  style={optionStyle(isActive)}
                >
                  {row.kind === 'result' ? (
                    <>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.data.label}
                        </span>
                        {row.data.sublabel && (
                          <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.data.sublabel}
                          </span>
                        )}
                      </span>
                      {row.data.badges?.includes('under_appeal') && <Badge variant="warning" dot>Under appeal</Badge>}
                      {row.data.archived && <Badge variant="neutral">Archived</Badge>}
                    </>
                  ) : (
                    <>
                      <span
                        aria-hidden="true"
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, color: 'var(--brand-primary)', flexShrink: 0 }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </span>
                      <span style={{ flex: 1, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-medium)' }}>{row.label}</span>
                    </>
                  )}
                </button>
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
